import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import AppShell from "@/components/AppShell";
import { EnterKeyHandler } from "@/components/Kit";
import Landing from "@/pages/Landing";
import { Privacy, Terms } from "@/pages/Legal";
import { LoginPage, ForgotPasswordPage, ResetPasswordPage, AuthCallback } from "@/pages/Auth";
import OwnerDashboard from "@/pages/owner/OwnerDashboard";
import Businesses from "@/pages/owner/Businesses";
import BusinessDetail from "@/pages/owner/BusinessDetail";
import PlatformUsers from "@/pages/owner/PlatformUsers";
import Groups from "@/pages/owner/Groups";
import OwnerAudit from "@/pages/owner/OwnerAudit";
import HospitalDashboard from "@/pages/hospital/HospitalDashboard";
import Patients from "@/pages/hospital/Patients";
import DoctorsDepartments from "@/pages/hospital/DoctorsDepartments";
import Appointments from "@/pages/hospital/Appointments";
import FollowUps from "@/pages/hospital/FollowUps";
import Opd from "@/pages/hospital/Opd";
import Ipd from "@/pages/hospital/Ipd";
import Maternity from "@/pages/hospital/Maternity";
import HospitalBilling from "@/pages/hospital/HospitalBilling";
import HospitalStock from "@/pages/hospital/HospitalStock";
import HospitalReports from "@/pages/hospital/HospitalReports";
import HospitalExpenses from "@/pages/hospital/HospitalExpenses";
import DoctorEarnings from "@/pages/hospital/DoctorEarnings";
import BirthRecords from "@/pages/hospital/BirthRecords";
import HospitalPackages from "@/pages/hospital/HospitalPackages";
import DeathRecords from "@/pages/hospital/DeathRecords";
import PatientDocuments from "@/pages/hospital/PatientDocuments";
import Attendance from "@/pages/shared/Attendance";
import PharmacyDashboard from "@/pages/pharmacy/PharmacyDashboard";
import Pos from "@/pages/pharmacy/Pos";
import Sales from "@/pages/pharmacy/Sales";
import B2B from "@/pages/pharmacy/B2B";
import Inventory from "@/pages/pharmacy/Inventory";
import Purchases from "@/pages/pharmacy/Purchases";
import Returns from "@/pages/pharmacy/Returns";
import Expenses from "@/pages/pharmacy/Expenses";
import PharmacyReports from "@/pages/pharmacy/PharmacyReports";
import BillScan from "@/pages/pharmacy/BillScan";
import LabDashboard from "@/pages/lab/LabDashboard";
import LabTests from "@/pages/lab/LabTests";
import LabOrders from "@/pages/lab/LabOrders";
import LabPartners from "@/pages/lab/LabPartners";
import LabReports from "@/pages/lab/LabReports";
import LabCategories from "@/pages/lab/LabCategories";
import LabPanels from "@/pages/lab/LabPanels";
import LabPackages from "@/pages/lab/LabPackages";
import LabReferrers from "@/pages/lab/LabReferrers";
import LabCentres from "@/pages/lab/LabCentres";
import LabAgents from "@/pages/lab/LabAgents";
import LabNewBill from "@/pages/lab/LabNewBill";
import LabBills from "@/pages/lab/LabBills";
import LabBillDetail from "@/pages/lab/LabBillDetail";
import LabPatients from "@/pages/lab/LabPatients";
import LabTransactions from "@/pages/lab/LabTransactions";
import LabWorklist from "@/pages/lab/LabWorklist";
import LabServices from "@/pages/lab/LabServices";
import LabSignatures from "@/pages/lab/LabSignatures";
import LabDailyBusiness from "@/pages/lab/LabDailyBusiness";
import LabBusinessAnalysis from "@/pages/lab/LabBusinessAnalysis";
import LabExpenses from "@/pages/lab/LabExpenses";
import LabDueReport from "@/pages/lab/LabDueReport";
import LabReferralBusiness from "@/pages/lab/LabReferralBusiness";
import LabDataExport from "@/pages/lab/LabDataExport";
import DiagnosticCases from "@/pages/lab/DiagnosticCases";
import Staff from "@/pages/shared/Staff";
import Shifts from "@/pages/shared/Shifts";
import Notifications from "@/pages/shared/Notifications";
import AuditLog from "@/pages/shared/AuditLog";
import SettingsPage from "@/pages/shared/SettingsPage";
import WorkspaceHome from "@/pages/shared/WorkspaceHome";
import Referrals from "@/pages/shared/Referrals";
import Payroll from "@/pages/shared/Payroll";
import FinanceReports from "@/pages/shared/FinanceReports";
import ConsolidatedReports from "@/pages/shared/ConsolidatedReports";
import ReportsCenter from "@/pages/shared/ReportsCenter";
import WhatsAppManager from "@/pages/shared/WhatsAppManager";
import AiCopilot from "@/pages/shared/AiCopilot";
import CentralInvoices from "@/pages/shared/CentralInvoices";

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/setup-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />

      <Route path="/owner" element={<AppShell ownerMode />}>
        <Route index element={<OwnerDashboard />} />
        <Route path="businesses" element={<Businesses />} />
        <Route path="businesses/:businessId" element={<BusinessDetail />} />
        <Route path="groups" element={<Groups />} />
        <Route path="users" element={<PlatformUsers />} />
        <Route path="audit" element={<OwnerAudit />} />
      </Route>

      <Route path="/app" element={<AppShell />}>
        <Route index element={<WorkspaceHome />} />
        <Route path="hospital" element={<HospitalDashboard />} />
        <Route path="hospital/patients" element={<Patients />} />
        <Route path="hospital/doctors" element={<DoctorsDepartments />} />
        <Route path="hospital/appointments" element={<Appointments />} />
        <Route path="hospital/follow-ups" element={<FollowUps />} />
        <Route path="hospital/opd" element={<Opd />} />
        <Route path="hospital/ipd" element={<Ipd />} />
        <Route path="hospital/maternity" element={<Maternity />} />
        <Route path="hospital/packages" element={<HospitalPackages />} />
        <Route path="hospital/birth" element={<BirthRecords />} />
        <Route path="hospital/death" element={<DeathRecords />} />
        <Route path="hospital/documents" element={<PatientDocuments />} />
        <Route path="hospital/billing" element={<HospitalBilling />} />
        <Route path="hospital/stock" element={<HospitalStock />} />
        <Route path="hospital/expenses" element={<HospitalExpenses />} />
        <Route path="hospital/earnings" element={<DoctorEarnings />} />
        <Route path="attendance" element={<Attendance />} />
        <Route path="hospital/reports" element={<HospitalReports />} />
        <Route path="pharmacy" element={<PharmacyDashboard />} />
        <Route path="pharmacy/pos" element={<Pos />} />
        <Route path="pharmacy/sales" element={<Sales />} />
        <Route path="pharmacy/b2b" element={<B2B />} />
        <Route path="pharmacy/inventory" element={<Inventory />} />
        <Route path="pharmacy/medicines" element={<Navigate to="/app/pharmacy/inventory?tab=medicines" replace />} />
        <Route path="pharmacy/suppliers" element={<Navigate to="/app/pharmacy/inventory?tab=suppliers" replace />} />
        <Route path="pharmacy/purchases" element={<Purchases />} />
        <Route path="pharmacy/bill-scan" element={<BillScan />} />
        <Route path="pharmacy/returns" element={<Returns />} />
        <Route path="pharmacy/expenses" element={<Expenses />} />
        <Route path="pharmacy/reports" element={<PharmacyReports />} />
        <Route path="lab" element={<LabDashboard />} />
        <Route path="lab/new-bill" element={<LabNewBill />} />
        <Route path="lab/bills" element={<LabBills preset="bills" />} />
        <Route path="lab/bills/:billId" element={<LabBillDetail />} />
        <Route path="lab/outsource" element={<LabBills preset="outsource" />} />
        <Route path="lab/ct-scan" element={<LabBills preset="ct" />} />
        <Route path="lab/patients" element={<LabPatients />} />
        <Route path="lab/transactions" element={<LabTransactions />} />
        <Route path="lab/today" element={<LabWorklist mode="today" />} />
        <Route path="lab/diagnostic/today" element={<DiagnosticCases mode="today" />} />
        <Route path="lab/diagnostic/search" element={<DiagnosticCases mode="search" />} />
        <Route path="lab/search-reports" element={<LabWorklist mode="search" />} />
        <Route path="lab/services" element={<LabServices />} />
        <Route path="lab/signatures" element={<LabSignatures />} />
        <Route path="lab/daily-business" element={<LabDailyBusiness />} />
        <Route path="lab/business-analysis" element={<LabBusinessAnalysis defaultTab="monthly" />} />
        <Route path="lab/case-wise" element={<LabBusinessAnalysis defaultTab="casewise" />} />
        <Route path="lab/expenses" element={<LabExpenses />} />
        <Route path="lab/due-report" element={<LabDueReport />} />
        <Route path="lab/referral-business" element={<LabReferralBusiness />} />
        <Route path="lab/data-export" element={<LabDataExport />} />
        <Route path="lab/tests" element={<LabTests />} />
        <Route path="lab/orders" element={<LabOrders />} />
        <Route path="lab/categories" element={<LabCategories />} />
        <Route path="lab/panels" element={<LabPanels />} />
        <Route path="lab/packages" element={<LabPackages />} />
        <Route path="lab/referrers" element={<LabReferrers />} />
        <Route path="lab/centres" element={<LabCentres />} />
        <Route path="lab/agents" element={<LabAgents />} />
        <Route path="lab/partners" element={<LabPartners />} />
        <Route path="lab/reports" element={<LabReports />} />
        <Route path="ai" element={<AiCopilot />} />
        <Route path="invoices" element={<CentralInvoices />} />
        <Route path="staff" element={<Staff />} />
        <Route path="staff/shifts" element={<Shifts />} />
        <Route path="referrals" element={<Referrals />} />
        <Route path="payroll" element={<Payroll />} />
        <Route path="finance" element={<FinanceReports />} />
        <Route path="reports/consolidated" element={<ConsolidatedReports />} />
        <Route path="reports/center" element={<ReportsCenter />} />
        <Route path="settings/whatsapp" element={<WhatsAppManager />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="audit" element={<AuditLog />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRouter />
          <EnterKeyHandler />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}